// Global "Audio-First"-Modus for kids who can't (yet) read fluently.
// When enabled, every narrative / question / answer-option auto-speaks on
// render, regardless of the TTS toggle in the story TopBar. Also broadcasts
// a custom event so already-mounted components can react without a prop drill.
//
// Usage:
//   const { enabled, toggle, available } = useAudioFirst();
//   const speakIfAudioFirst = useAutoSpeak(speak); // fires whenever text changes

import { useCallback, useEffect, useState } from 'react';

const LS_KEY = 'lernwelt.audio_first.enabled';
const EVT = 'lernwelt:audio-first-changed';

function initialEnabled(): boolean {
  try { return localStorage.getItem(LS_KEY) === '1'; } catch { return false; }
}

export function isAudioFirstEnabled(): boolean {
  return initialEnabled();
}

export function useAudioFirst() {
  const available = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [enabled, setEnabled] = useState<boolean>(initialEnabled());

  useEffect(() => {
    function onChanged(e: Event) {
      setEnabled(((e as CustomEvent<boolean>).detail) ?? initialEnabled());
    }
    window.addEventListener(EVT, onChanged as EventListener);
    return () => window.removeEventListener(EVT, onChanged as EventListener);
  }, []);

  const toggle = useCallback(() => {
    setEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem(LS_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      try {
        window.dispatchEvent(new CustomEvent<boolean>(EVT, { detail: next }));
      } catch { /* ignore */ }
      return next;
    });
  }, []);

  const setFromSettings = useCallback((next: boolean) => {
    setEnabled(next);
    try { localStorage.setItem(LS_KEY, next ? '1' : '0'); } catch { /* ignore */ }
    try {
      window.dispatchEvent(new CustomEvent<boolean>(EVT, { detail: next }));
    } catch { /* ignore */ }
  }, []);

  return { enabled, available, toggle, setEnabled: setFromSettings };
}
